/**
 * /v1 contract additions for R2 (global launch) and R3 (live): teams, tournament,
 * war, pass, subscription, push, events, collections, trading, duels, cups, packs.
 * Every name is exported; requests are validated with these on the server.
 */
import { FriendStreet, PublicPlayer, RewardSchema, RoundRequestR1 } from './api-r1.js';
import { LevelSchema } from './level.js';
import { z } from 'zod';

const Id = z.uuid();
const Ms = z.number().int();
const LevelJson = LevelSchema;

// ---------- CP-02 teams ----------
export const TeamCreate = z.object({ name: z.string().min(3).max(20), open: z.boolean(), badge: z.string().regex(/^[a-z0-9_]{1,24}$/).optional() });
export const TeamSummary = z.object({ id: Id, name: z.string(), badge: z.string(), open: z.boolean(), members: z.number().int() });
export const TeamView = z.object({
  team: TeamSummary.omit({ members: true }).extend({ members: z.number().int(), leaderId: Id.nullable() }),
  /** lastActiveAt: shown to teammates only (absent when a non-member views the team) */
  members: z.array(z.object({ player: PublicPlayer, role: z.enum(['leader', 'co_leader', 'member']), joinedAt: Ms, lastActiveAt: Ms.optional() })),
  you: z.object({ role: z.enum(['leader', 'co_leader', 'member']) }).nullable(),
  messages: z.array(z.object({ id: z.number().int(), playerId: Id.nullable(), messageId: z.number().int().min(1).max(20), at: Ms })),
  help: z.array(z.object({ id: Id, requesterId: Id, rareItem: z.string(), qty: z.number().int(), filled: z.number().int(), expiresAt: Ms, helpedByYou: z.boolean() })),
  joinRequests: z.array(z.object({ player: PublicPlayer, at: Ms })),
});
export const TeamJoinResponse = z.object({ status: z.enum(['joined', 'requested']) });
export const TeamMessage = z.object({ messageId: z.number().int().min(1).max(20) });
export const TeamHelpRequest = z.object({ rareItem: z.string(), qty: z.number().int().min(1).max(3).optional() });
export const TeamHelpCreated = z.object({ id: Id, expiresAt: Ms });
export const TeamHelpGiven = z.object({ reward: RewardSchema });

// ---------- CP-03 rush tournament ----------
export const TournamentView = z.object({
  id: z.string(),
  status: z.enum(['open', 'closed']),
  opensAt: Ms,
  closesAt: Ms,
  levels: z.array(z.object({ idx: z.number().int(), levelId: z.string(), seed: z.number().int(), level: LevelJson })),
  you: z.object({ best: z.record(z.string(), z.number().int()), total: z.number().int(), rank: z.number().int() }).nullable(),
  leaderboard: z.array(z.object({ playerId: Id, name: z.string(), total: z.number().int(), rank: z.number().int(), you: z.boolean() })),
});
export const TournamentRoundRequest = z.object({ idx: z.number().int().min(0).max(4), round: RoundRequestR1 });
export const TournamentRoundResponse = z.object({ score: z.number().int(), best: z.number().int(), total: z.number().int() });

// ---------- CP-04 team war ----------
export const WarEntry = z.object({ teamId: Id, name: z.string(), served: z.number().int() });
/** The chest each rank's contributors get (1st, 2nd, 3rd…; `others` below the listed ranks). */
export const WarChests = z.object({ byRank: z.array(RewardSchema), others: RewardSchema });
export const WarView = z.object({
  warId: Id,
  startsAt: Ms,
  endsAt: Ms,
  /** the server clock when answered: the countdown to endsAt runs on it, not on the device's */
  serverTime: Ms.optional(),
  teamId: Id,
  yourServed: z.number().int(),
  entries: z.array(WarEntry.extend({ rank: z.number().int() })),
  chests: WarChests.optional(),
});
/**
 * GET /v1/war/last: the caller's most recent finished war, with the final standings (the ranks the chests were
 * paid on), your team, what you served and the chest your team's contributors got (null if you served nothing).
 */
export const WarResult = z.object({
  warId: Id,
  startsAt: Ms,
  endsAt: Ms,
  finishedAt: Ms,
  serverTime: Ms,
  teamId: Id,
  yourServed: z.number().int(),
  yourChest: RewardSchema.nullable(),
  entries: z.array(WarEntry.extend({ rank: z.number().int(), chest: RewardSchema })),
  chests: WarChests,
});
/**
 * Redis pub/sub `war:{warId}` payload, fanned out by apps/realtime. The last one of a war has `type: 'war.end'`
 * and `final: true` (and still the final board, so a hub that does not read `type` shows it as a board).
 */
export const WarBoardMessage = z.object({ warId: Id, entries: z.array(WarEntry), at: Ms, type: z.literal('war.end').optional(), final: z.boolean().optional() });

// ---------- SY-03 notifications ----------
export const PushType = z.enum(['offline_ready', 'friend_helped', 'league_ending', 'event_started', 'daily_puzzle', 'streak_risk', 'win_back']);
export type PushType = z.infer<typeof PushType>;
/**
 * Native tokens: Android sends its FCM registration token (`fcm`), iOS its APNs device token
 * (`apns`, hex), as @capacitor/push-notifications hands them out. `platform` is informative.
 */
export const PushRegister = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('webpush'), endpoint: z.url().max(1000), keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(64) }) }),
  z.object({ kind: z.literal('fcm'), token: z.string().min(8).max(512), platform: z.enum(['android', 'ios']).optional() }),
  z.object({ kind: z.literal('apns'), token: z.string().min(8).max(200), platform: z.literal('ios').optional() }),
]);
export const NotificationPrefs = z.object({ types: z.partialRecord(PushType, z.boolean()).optional(), tzOffsetMin: z.number().int().min(-720).max(840).optional() });
export const NotificationPrefsResponse = z.object({ types: z.record(PushType, z.boolean()), tzOffsetMin: z.number().int() });

// ---------- LO-01 events ----------
const EventReward = RewardSchema.extend({ stickerDraw: z.boolean().optional() });
/** Event config file format (admin publish body and content/events/*.json). */
export const EventConfig = z.object({
  id: z.string().regex(/^[a-z0-9-]{2,32}$/),
  name: z.string().max(60),
  version: z.number().int().min(1),
  theme: z.object({ palette: z.array(z.string()).max(8).optional(), music: z.string().optional(), background: z.string().optional() }),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  /** a generation spec id (`rush:<key>:<idx>` or `daily:<YYYY-MM-DD>`) or inline level JSON */
  /** real level files (content/events/<id>/levels) */
  levelIds: z.array(z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/)).min(1).max(20),
  rewards: z.object({
    milestones: z.array(z.object({ points: z.number().int().min(1), reward: EventReward })).max(20),
    leaderboard: z.array(z.object({ rankTo: z.number().int().min(1), reward: EventReward })).max(10),
  }),
  leaderboard: z.object({ size: z.number().int().min(10).max(500) }),
  artPack: z.object({ manifestUrl: z.url(), sizeBytes: z.number().int().min(0), background: z.boolean() }),
  cup: z.object({ city: z.boolean(), creators: z.boolean() }).optional(),
});
export type EventConfig = z.infer<typeof EventConfig>;
export const EventView = z.object({
  id: z.string(),
  name: z.string(),
  version: z.number().int(),
  theme: EventConfig.shape.theme,
  startsAt: Ms,
  endsAt: Ms,
  levels: z.array(z.object({ idx: z.number().int(), levelId: z.string(), seed: z.number().int(), level: LevelJson })),
  rewards: EventConfig.shape.rewards,
  artPack: EventConfig.shape.artPack,
  /** CP-06 cups this event runs */
  cup: EventConfig.shape.cup,
  you: z.object({ points: z.number().int(), rounds: z.number().int(), milestones: z.number().int() }),
});
export const EventsCurrent = z.object({ events: z.array(EventView) });
export const EventProgressRequest = z.object({ idx: z.number().int().min(0), round: RoundRequestR1 });
export const EventProgressResponse = z.object({ points: z.number().int(), gained: z.number().int(), milestonesReached: z.array(z.number().int()) });
export const EventLeaderboard = z.object({ eventId: z.string(), entries: z.array(z.object({ playerId: Id, name: z.string(), points: z.number().int(), rank: z.number().int() })), you: z.object({ points: z.number().int(), rank: z.number().int() }).nullable() });
/**
 * `flags` and `switches` publish as a merge into the live map: only the entries sent change. `replace: true`
 * stores the value as the whole map instead (every other key always replaces its value).
 */
export const AdminConfigPublish = z.object({ key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/), value: z.unknown(), replace: z.boolean().optional() });

// ---------- MT-07 / MT-08 collections ----------
export const Collections = z.object({
  customers: z.object({ met: z.array(z.object({ id: z.string(), timesServed: z.number().int(), firstMetAt: Ms })), total: z.number().int() }),
  album: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      reward: z.object({ costume: z.string().optional(), decor: z.string().optional() }),
      stickers: z.array(z.object({ id: z.string(), rarity: z.enum(['common', 'rare', 'gold']), count: z.number().int() })),
      complete: z.boolean(),
      /** the set's reward (costume or decor) is owned: earned by completing the set, or bought */
      rewardOwned: z.boolean().optional(),
    }),
  ),
});
const Odds = z.object({ common: z.number(), rare: z.number(), gold: z.number() });
export const StickerOdds = z.object({ sources: z.record(z.string(), z.object({ rarity: Odds, perSticker: Odds })) });

// ---------- MT-09 costumes ----------
export const Costumes = z.object({ equipped: z.string(), owned: z.array(z.string()), all: z.array(z.string()) });
/** Pip looks (GET /v1/skins): the one worn, the owned ones (classic always) and every look in wardrobe order. */
export const Skins = z.object({ equipped: z.string(), owned: z.array(z.string()), all: z.array(z.string()) });

// ---------- SO-07 shop tour ----------
/** `decor`: spot → decor id, on the shops that have decor placed (shown in the interior) */
export const ShopTour = z.object({
  player: PublicPlayer,
  shops: z.record(z.string(), z.object({ upgrades: z.record(z.string(), z.enum(['a', 'b', 'c'])), decor: z.record(z.string(), z.string()).optional() })),
  hearts: z.number().int(),
  rank: z.number().int(),
  likedToday: z.boolean(),
});
export const PopularShops = z.object({ shops: z.array(z.object({ player: PublicPlayer, hearts: z.number().int(), rank: z.number().int() })) });
/** FriendStreet + hearts on the shop sign (SO-07). */
export const FriendStreetR2 = z.object({ hearts: z.number().int() });
/** GET /v1/friends/:id/street as the game reads it: the R1 street, plus the sign's hearts where SO-07 is on. */
export const FriendStreetView = FriendStreet.extend({ hearts: FriendStreetR2.shape.hearts.optional() });
/** POST /v1/shops/:shopId/like (social/likes.ts likeShop): the sign's new heart count. */
export const ShopLikeResponse = ShopTour.pick({ hearts: true });

// ---------- SO-08 sticker trades ----------
export const TradeOffer = z.object({ to: Id, give: z.string(), want: z.string() });
export const Trade = z.object({ id: Id, fromId: Id, toId: Id, give: z.string(), want: z.string(), status: z.enum(['offered', 'accepted', 'declined', 'cancelled', 'expired']), createdAt: Ms, expiresAt: Ms });
export const TradesResponse = z.object({ incoming: z.array(Trade), outgoing: z.array(Trade) });

// ---------- SY-01 client errors ----------
export const ClientErrors = z.object({
  errors: z.array(z.object({ message: z.string().max(2000), stack: z.string().max(8000).optional(), context: z.record(z.string(), z.unknown()).optional() })).min(1).max(20),
});

// ---------- CP-05 duels (internal, HMAC; see apps/realtime/internal/apiclient) ----------
export const DuelCreate = z.object({ players: z.array(z.object({ id: Id, rating: z.number().int() })).min(1).max(2), bot: z.boolean() });
export const BotTimelineEntry = z.object({ t: Ms, served: z.number().int(), bag: z.array(z.number().int()), taps: z.number().int() });
export const DuelCreated = z.object({
  duelId: Id,
  levelId: z.string(),
  seed: z.number().int(),
  bot: z.object({ name: z.string(), rating: z.number().int(), result: z.enum(['win', 'fail']), timeline: z.array(BotTimelineEntry) }).optional(),
  names: z.record(z.string(), z.string()).optional(),
});
export const DuelVerify = z.object({
  duelId: Id.optional(),
  playerId: Id,
  header: z.object({ rulesVersion: z.number().int(), levelId: z.string(), levelHash: z.string(), seed: z.number().int(), assist: z.number().int(), rescueThreshold: z.number().int(), maxRescues: z.number().int() }),
  log: z.string().max(20000).regex(/^[A-Za-z0-9_-]*$/),
  claimed: z.object({ result: z.enum(['win', 'fail']), taps: z.number().int(), stars: z.number().int(), coins: z.number().int() }),
});
export const DuelOutcome = z.object({ result: z.enum(['win', 'fail']), taps: z.number().int(), durationMs: Ms, itemsServed: z.number().int() });
export const DuelVerified = z.object({ verified: z.boolean(), outcome: DuelOutcome, reason: z.string().optional() });
export const DuelResult = z.object({ duelId: Id.optional(), winner: z.string().nullable(), reason: z.string().optional(), forfeits: z.array(z.string()).optional() });
export const DuelRating = z.object({ rating: z.number().int(), played: z.number().int(), won: z.number().int() });
/** One of the caller's duels (GET /v1/duels/:id; the entries of GET /v1/duels/history). */
export const DuelEntry = z.object({ duelId: Id, levelId: z.string(), at: Ms, opponent: z.object({ id: z.string(), name: z.string(), rating: z.number().int() }), result: z.enum(['win', 'loss', 'draw', 'none']), ratingBefore: z.number().int(), ratingAfter: z.number().int().nullable(), reason: z.string().nullable() });
export const DuelHistory = z.object({ duels: z.array(DuelEntry) });

// ---------- CP-06 cups ----------
export const CreatorCode = z.object({ code: z.string().regex(/^[A-Za-z0-9_]{3,24}$/) });
export const CupResults = z.object({
  eventId: z.string(),
  name: z.string(),
  countries: z.array(z.object({ country: z.string(), points: z.number().int(), players: z.number().int(), rank: z.number().int() })),
  creators: z.array(z.object({ code: z.string(), name: z.string(), points: z.number().int(), players: z.number().int(), rank: z.number().int() })),
});
export const CreatorsPublish = z.object({ creators: z.array(z.object({ code: CreatorCode.shape.code, name: z.string().max(40) })).min(1).max(500) });

// ---------- SO-09 customer steal ----------
export const StealResponse = z.object({ stealId: Id, customerId: z.string(), reward: RewardSchema });
export const Steal = z.object({ id: Id, thiefId: Id, victimId: Id, customerId: z.string(), createdAt: Ms, takeBackUntil: Ms, takenBack: z.boolean() });
/**
 * Each side separately, newest first, 50 at most (`more`: older ones exist; GET /v1/steals?before=<createdAt>),
 * the limits the client used to copy, today's remaining steals, and the caller's visitor badges.
 */
export const StealsResponse = z.object({
  byYou: z.array(Steal),
  onYou: z.array(Steal),
  more: z.object({ byYou: z.boolean(), onYou: z.boolean() }).optional(),
  limits: z.object({ perDay: z.number().int(), takeBackMs: Ms, reward: RewardSchema }).optional(),
  stealsLeftToday: z.number().int().optional(),
  visitorBadges: z.number().int().optional(),
});

// ---------- SO-10 city vote ----------
export const CityVoteRequest = z.object({ city: z.string().regex(/^[a-z_]{2,32}$/) });
/** `names`: English display names by city id (clients translate `city.{id}.name` when they have it). */
export const CityVoteView = z.object({ month: z.string(), candidates: z.array(z.string()), yourVote: z.string().nullable(), names: z.record(z.string(), z.string()).optional() });
/** `leaders`: the cities with the most votes (two or three = a tie; empty before the first vote). */
export const CityVoteResults = z.object({
  month: z.string(),
  region: z.string().nullable(),
  totals: z.array(z.object({ city: z.string(), votes: z.number().int(), share: z.number(), name: z.string().optional() })),
  votes: z.number().int(),
  leaders: z.array(z.string()).optional(),
});

// ---------- LO-02 content packs ----------
export const PackFile = z.object({ path: z.string().regex(/^[a-z0-9][a-z0-9_./-]{0,127}$/), kind: z.enum(['levels', 'items', 'shops', 'customers', 'music', 'art']), sha256: z.string().regex(/^[0-9a-f]{64}$/), size: z.number().int().min(0) });
/** `minRulesVersion` (roadmap-3k §8.1): the oldest core RULES_VERSION that plays every level of the pack (default 1). */
const MinRulesVersion = z.number().int().min(1);
/**
 * `mechanics` (roadmap-3k §8.1 mechanics gate): the mechanics the pack's levels use, as
 * tools/src/city-pack.ts writes them (core `Mechanic` ids). Every roadmap mechanic is rules v2, so
 * this list, not `minRulesVersion`, says which of them a pack needs. Packs published before the
 * list existed have none. Publishing checks the ids' shape (the API also refuses one its engine
 * does not play); what clients read takes any string, so a list naming mechanics newer than the
 * client never fails its parse, only that pack's gate.
 */
export const PackMechanics = z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,39}$/)).max(64);
export const PackPublish = z.object({
  city: z.string().regex(/^[a-z_]{2,32}$/),
  version: z.number().int().min(1),
  minRulesVersion: MinRulesVersion.optional(),
  mechanics: PackMechanics.optional(),
  files: z.array(PackFile).min(1).max(2000),
});
/**
 * A client whose RULES_VERSION is below `minRulesVersion`, or whose engine lacks one of
 * `mechanics` (core SUPPORTED_MECHANICS), must not download the pack (it shows "update the app").
 */
export const PackSummary = z.object({ city: z.string(), version: z.number().int(), minRulesVersion: MinRulesVersion, mechanics: z.array(z.string()).optional(), totalBytes: z.number().int(), publishedAt: Ms });
export const PackManifest = PackSummary.extend({ files: z.array(PackFile.extend({ url: z.url() })) });
export const PacksResponse = z.object({ packs: z.array(PackSummary), budgetBytes: z.number().int() });

